import { useToggle } from "@evergis/ui";
import { useCallback, useEffect } from "react";
import isFunction from "lodash/isFunction";

export type BulkOperationsToggleConfig<T> = {
  column: keyof T;
  reverse?: boolean;
  items: T[];
  setItems: (items: T[]) => void;
  excludeItem?: (item: T) => boolean;
  isChecked?: (item: T) => boolean;
};

export const useBulkOperationsToggle = <T>({
  column,
  reverse = false,
  items,
  setItems,
  isChecked,
  excludeItem,
}: BulkOperationsToggleConfig<T>): [boolean, VoidFunction] => {
  const [checked, toggleChecked, setChecked] = useToggle();

  const toggleBulkOperations = useCallback(() => {
    setItems(
      items.map(item => {
        if (isFunction(excludeItem) && excludeItem(item)) {
          return item;
        }

        return {
          ...item,
          [column]: reverse ? checked : !checked,
        };
      }),
    );
    toggleChecked();
  }, [toggleChecked, checked, column, items, setItems, reverse, excludeItem]);

  useEffect(() => {
    if (items?.length > 0) {
      const notCheckedItem = items.find(item => {
        if (isFunction(excludeItem) && excludeItem(item)) {
          return false;
        }

        return isFunction(isChecked) ? !isChecked(item) : reverse ? item[column] : !item[column];
      });

      setChecked(!notCheckedItem);
    }
  }, [items, checked, reverse]);

  return [checked, toggleBulkOperations];
};
